
import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { createCurrency, updateCurrency } from "../../redux/Slices/CurrencySlice";
import { getAllRegionSettings } from "../../redux/Actions/RegionSettingsAction";
import { Currency, CurrencyCreateDto, CurrencyUpdateDto, CurrencyActionModalProps } from "../../modules/shared/model";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { X } from "lucide-react";

const CurrencyActionModal: React.FC<CurrencyActionModalProps> = ({ isOpen, onClose, currency, mode }) => {
  const dispatch = useAppDispatch();
  const { regionSettings } = useAppSelector((state) => state.regionSettings);
  const { loading } = useAppSelector((state) => state.currencies);

  const [formData, setFormData] = useState<CurrencyCreateDto>({
    name: "",
    initial: "",
    symbol: "",
    isDefault: false,
    exchangeRate: 0,
    MaxLimit: 0,
    minLimit: 0,
    regularCommission: 0,
    emergencyHolidayCommission: 0,
    emergencyNightCommission: 0,
    SettingDecimalPlaces: 2,
    SettingThousandsSeparator: ",",
    SettingDecimalSeparator: ".",
    SettingSymbolPosition: "before",
    emergencyFee: 0,
    platformFee: 0,
    transactionFee: 0,
    emergencyCommissionRate: 0,
    regularCommissionRate: 0,
    regions: [],
  });

  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);

  // Load regions on component mount
  useEffect(() => {
    if (isOpen) {
      dispatch(getAllRegionSettings({ page: 1, limit: 100 }));
    }
  }, [dispatch, isOpen]);

  // Populate form when editing
  useEffect(() => {
    if (currency && mode === "edit") {
      setFormData({
        name: currency.name,
        initial: currency.initial,
        symbol: currency.symbol || "",
        isDefault: currency.isDefault || false,
        exchangeRate: currency.exchangeRate || 0,
        MaxLimit: currency.MaxLimit || 0,
        minLimit: currency.minLimit || 0,
        regularCommission: currency.regularCommission || 0,
        emergencyHolidayCommission: currency.emergencyHolidayCommission || 0,
        emergencyNightCommission: currency.emergencyNightCommission || 0,
        SettingDecimalPlaces: currency.SettingDecimalPlaces || 2,
        SettingThousandsSeparator: currency.SettingThousandsSeparator || ",",
        SettingDecimalSeparator: currency.SettingDecimalSeparator || ".",
        SettingSymbolPosition: currency.SettingSymbolPosition || "before",
        emergencyFee: currency.emergencyFee || 0,
        platformFee: currency.platformFee || 0,
        transactionFee: currency.transactionFee || 0,
        emergencyCommissionRate: currency.emergencyCommissionRate || 0,
        regularCommissionRate: currency.regularCommissionRate || 0,
        regions: currency.regions || [],
      });
      setSelectedRegions(currency.regions?.map(r => r.id) || []);
    } else if (mode === "create") {
      // Reset form for create mode
      setFormData({
        name: "",
        initial: "",
        symbol: "",
        isDefault: false,
        exchangeRate: 0,
        MaxLimit: 0,
        minLimit: 0,
        regularCommission: 0,
        emergencyHolidayCommission: 0,
        emergencyNightCommission: 0,
        SettingDecimalPlaces: 2,
        SettingThousandsSeparator: ",",
        SettingDecimalSeparator: ".",
        SettingSymbolPosition: "before",
        emergencyFee: 0,
        platformFee: 0,
        transactionFee: 0,
        emergencyCommissionRate: 0,
        regularCommissionRate: 0,
        regions: [],
      });
      setSelectedRegions([]);
    }
  }, [currency, mode]);

  const handleInputChange = (field: keyof CurrencyCreateDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRegionToggle = (regionId: string) => {
    setSelectedRegions(prev => 
      prev.includes(regionId) 
        ? prev.filter(id => id !== regionId)
        : [...prev, regionId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const regionsData = regionSettings.filter(region => selectedRegions.includes(region.id));
    
    const submitData = {
      ...formData,
      regions: regionsData,
    };

    try {
      if (mode === "create") {
        await dispatch(createCurrency(submitData)).unwrap();
      } else if (mode === "edit" && currency) {
        await dispatch(updateCurrency({ id: currency.id, data: submitData })).unwrap();
      }
      onClose();
    } catch (error) {
      console.error("Failed to save currency:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"   style={{ textAlign: "left" }}>
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">
              {mode === "create" ? "Create Currency" : mode === "edit" ? "Edit Currency Settings" : "View Currency Settings"}
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Basic Settings */}
            <div>
              <h3 className="text-lg font-medium mb-4">Basic Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    placeholder="Currency name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Code</label>
                  <input
                    type="text"
                    value={formData.initial}
                    onChange={(e) => handleInputChange("initial", e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    placeholder="Currency code"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Symbol</label>
                  <input
                    type="text"
                    value={formData.symbol || ""}
                    onChange={(e) => handleInputChange("symbol", e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    placeholder="Currency symbol"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Exchange Rate</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.exchangeRate || 0}
                    onChange={(e) => handleInputChange("exchangeRate", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    placeholder="Exchange rate"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isDefault || false}
                    onChange={(e) => handleInputChange("isDefault", e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700">Default Currency</label>
                </div>
              </div>
            </div>

            {/* Commission Settings */}
            <div>
              <h3 className="text-lg font-medium mb-4">Commission Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Regular Commission (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.regularCommission || 0}
                    onChange={(e) => handleInputChange("regularCommission", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Regular Commission Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.regularCommissionRate || 0}
                    onChange={(e) => handleInputChange("regularCommissionRate", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Emergency Holiday Commission (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.emergencyHolidayCommission || 0}
                    onChange={(e) => handleInputChange("emergencyHolidayCommission", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Emergency Night Commission (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.emergencyNightCommission || 0}
                    onChange={(e) => handleInputChange("emergencyNightCommission", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Emergency Commission Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.emergencyCommissionRate || 0}
                    onChange={(e) => handleInputChange("emergencyCommissionRate", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Fee Settings */}
            <div>
              <h3 className="text-lg font-medium mb-4">Fee Settings</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Transaction Fee (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.transactionFee || 0}
                    onChange={(e) => handleInputChange("transactionFee", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Platform Fee (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.platformFee || 0}
                    onChange={(e) => handleInputChange("platformFee", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Emergency Fee (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.emergencyFee || 0}
                    onChange={(e) => handleInputChange("emergencyFee", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Limits */}
            <div>
              <h3 className="text-lg font-medium mb-4">Transaction Limits</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Minimum Limit</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.minLimit || 0}
                    onChange={(e) => handleInputChange("minLimit", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Maximum Limit</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.MaxLimit || 0}
                    onChange={(e) => handleInputChange("MaxLimit", parseFloat(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Display Settings */}
            <div>
              <h3 className="text-lg font-medium mb-4">Display Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Decimal Places</label>
                  <input
                    type="number"
                    min="0"
                    max="6"
                    value={formData.SettingDecimalPlaces || 2}
                    onChange={(e) => handleInputChange("SettingDecimalPlaces", parseInt(e.target.value) || 2)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Thousands Separator</label>
                  <input
                    type="text"
                    value={formData.SettingThousandsSeparator || ","}
                    onChange={(e) => handleInputChange("SettingThousandsSeparator", e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Decimal Separator</label>
                  <input
                    type="text"
                    value={formData.SettingDecimalSeparator || "."}
                    onChange={(e) => handleInputChange("SettingDecimalSeparator", e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Symbol Position</label>
                  <select
                    value={formData.SettingSymbolPosition || "before"}
                    onChange={(e) => handleInputChange("SettingSymbolPosition", e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  >
                    <option value="before">Before</option>
                    <option value="after">After</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Regional Settings */}
            <div>
              <h3 className="text-lg font-medium mb-4">Regional Settings</h3>
              <div className="grid grid-cols-3 gap-2">
                {regionSettings.map((region) => (
                  <label key={region.id} className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={selectedRegions.includes(region.id)}
                      onChange={() => handleRegionToggle(region.id)}
                      className="rounded border-gray-300" 
                    />
                    <span className="text-sm">{region.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="p-6 border-t flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} type="button">
              Cancel
            </Button>
            {mode !== "view" && (
              <Button type="submit" disabled={loading}>
                {loading ? "Saving..." : mode === "create" ? "Create Currency" : "Save Changes"}
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CurrencyActionModal;